import { mediaItemId, type MediaItemId } from "./MediaItem";
import { usersId, type UsersId } from "./Users";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for media_variant */
export type MediaVariantId = string & { __flavor: "MediaVariantId" };

/**
 * Represents the table public.media_variant
 * @uniqueConstraints {"media_variant_pkey":{"name":"media_variant_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"media_variant_specs_unique":{"name":"media_variant_specs_unique","isPrimary":false,"isUnique":true,"columns":[{"name":"media_item_id","definition":"media_item_id","predicate":null},{"name":"format","definition":"format","predicate":null},{"name":"width","definition":"width","predicate":null},{"name":"height","definition":"height","predicate":null},{"name":"quality","definition":"quality","predicate":null},{"name":"transform_params","definition":"transform_params","predicate":null}]},"idx_media_variant_media_item":{"name":"idx_media_variant_media_item","isPrimary":false,"isUnique":false,"columns":[{"name":"media_item_id","definition":"media_item_id","predicate":null}]},"idx_media_variant_published_at":{"name":"idx_media_variant_published_at","isPrimary":false,"isUnique":false,"columns":[{"name":"published_at","definition":"published_at","predicate":null}]},"idx_media_variant_content_hash":{"name":"idx_media_variant_content_hash","isPrimary":false,"isUnique":false,"columns":[{"name":"content_hash","definition":"content_hash","predicate":null}]},"idx_media_variant_created_at":{"name":"idx_media_variant_created_at","isPrimary":false,"isUnique":false,"columns":[{"name":"created_at","definition":"created_at","predicate":null}]},"idx_media_variant_format":{"name":"idx_media_variant_format","isPrimary":false,"isUnique":false,"columns":[{"name":"format","definition":"format","predicate":null}]}}
 */
export default interface MediaVariantTable {
  id: ColumnType<MediaVariantId, MediaVariantId | undefined, MediaVariantId>;

  mediaItemId: ColumnType<MediaItemId, MediaItemId | undefined, MediaItemId>;

  format: ColumnType<string, string, string>;

  width: ColumnType<number | null, number | null, number | null>;

  height: ColumnType<number | null, number | null, number | null>;

  quality: ColumnType<number | null, number | null, number | null>;

  transformParams: ColumnType<unknown | null, unknown | null, unknown | null>;

  fileSize: ColumnType<number, number, number>;

  privateKey: ColumnType<string, string, string>;

  publicKey: ColumnType<string | null, string | null, string | null>;

  contentHash: ColumnType<string, string, string>;

  publishedAt: ColumnType<Date | null, Date | string | null, Date | string | null>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  createdBy: ColumnType<UsersId, UsersId, UsersId>;
}

export type MediaVariant = Selectable<MediaVariantTable>;

export type NewMediaVariant = Insertable<MediaVariantTable>;

export type MediaVariantUpdate = Updateable<MediaVariantTable>;

export const mediaVariantId = z.string() as unknown as z.Schema<MediaVariantId>;

export const mediaVariant = z.object({
  id: mediaVariantId,
  mediaItemId: mediaItemId,
  format: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  quality: z.number().nullable(),
  transformParams: z.unknown().nullable(),
  fileSize: z.number(),
  privateKey: z.string(),
  publicKey: z.string().nullable(),
  contentHash: z.string(),
  publishedAt: z.date().nullable(),
  createdAt: z.date(),
  createdBy: usersId,
});

export const mediaVariantInitializer = z.object({
  id: mediaVariantId.optional(),
  mediaItemId: mediaItemId.optional(),
  format: z.string(),
  width: z.number().optional().nullable(),
  height: z.number().optional().nullable(),
  quality: z.number().optional().nullable(),
  transformParams: z.unknown().optional().nullable(),
  fileSize: z.number(),
  privateKey: z.string(),
  publicKey: z.string().optional().nullable(),
  contentHash: z.string(),
  publishedAt: z.date().optional().nullable(),
  createdAt: z.date().optional(),
  createdBy: usersId,
});

export const mediaVariantMutator = z.object({
  id: mediaVariantId.optional(),
  mediaItemId: mediaItemId.optional(),
  format: z.string().optional(),
  width: z.number().optional().nullable(),
  height: z.number().optional().nullable(),
  quality: z.number().optional().nullable(),
  transformParams: z.unknown().optional().nullable(),
  fileSize: z.number().optional(),
  privateKey: z.string().optional(),
  publicKey: z.string().optional().nullable(),
  contentHash: z.string().optional(),
  publishedAt: z.date().optional().nullable(),
  createdAt: z.date().optional(),
  createdBy: usersId.optional(),
});

// ── constraints for MediaVariant ─────────────────────────────────
export const MediaVariantConstraints = {
  media_variant_pkey: {
    name: "media_variant_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  media_variant_specs_unique: {
    name: "media_variant_specs_unique",
    isPrimary: false,
    isUnique: true,
    columns: [
      { name: "media_item_id", definition: "media_item_id", predicate: null },
      { name: "format", definition: "format", predicate: null },
      { name: "width", definition: "width", predicate: null },
      { name: "height", definition: "height", predicate: null },
      { name: "quality", definition: "quality", predicate: null },
      { name: "transform_params", definition: "transform_params", predicate: null },
    ],
  },
  idx_media_variant_media_item: {
    name: "idx_media_variant_media_item",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "media_item_id", definition: "media_item_id", predicate: null }],
  },
  idx_media_variant_published_at: {
    name: "idx_media_variant_published_at",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "published_at", definition: "published_at", predicate: null }],
  },
  idx_media_variant_content_hash: {
    name: "idx_media_variant_content_hash",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "content_hash", definition: "content_hash", predicate: null }],
  },
  idx_media_variant_created_at: {
    name: "idx_media_variant_created_at",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "created_at", definition: "created_at", predicate: null }],
  },
  idx_media_variant_format: {
    name: "idx_media_variant_format",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "format", definition: "format", predicate: null }],
  },
} as const;
export type MediaVariantConstraintsType = typeof MediaVariantConstraints;
